/**
 * Customer Dashboard - Jobs metrics for authenticated customer only.
 * Calls /customer/dash/jobs-metrics (no customer selector).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import { TIMEZONES_BY_REGION, formatTimezoneLabel } from "../ui/Timezones";
import ApplicationMultiSelectDropdown from "../components/ApplicationMultiSelectDropdown";
import { authedGet } from "../api/customerAuthedFetch";
import InfoTip from "../components/InfoTip";

const RANGE_OPTS = ["1h", "3h", "12h", "1d", "1w", "2w", "1m", "3m"];
const GRAN_OPTS = ["1m", "5m", "15m", "1h", "1d"];
const METRIC_OPTS = [
  { key: "submitted", label: "Jobs Submitted" },
  { key: "processed", label: "Jobs Processed" },
];
const CHART_OPTS = ["line", "bar"];

const REFRESH_OPTS = [
  { key: "off", label: "Manual", ms: 0 },
  { key: "5m", label: "Every 5 min", ms: 300_000 },
  { key: "10m", label: "Every 10 min", ms: 600_000 },
  { key: "30m", label: "Every 30 min", ms: 1_800_000 },
  { key: "1h", label: "Every 1 hour", ms: 3_600_000 },
];

function granMinutes(g) {
  const m = { "1m": 1, "5m": 5, "15m": 15, "1h": 60, "1d": 1440 };
  return m[g] ?? 5;
}
function rangeMinutes(r) {
  const m = { "1h": 60, "3h": 180, "12h": 720, "1d": 1440, "1w": 10080, "2w": 20160, "1m": 43200, "3m": 129600 };
  return m[r] ?? 720;
}
function enforceGranularity(rangeKey, granKey) {
  const r = rangeMinutes(rangeKey);
  let g = granMinutes(granKey);
  if (r >= 43200) g = Math.max(g, 60);
  if (g === 1) return "1m";
  if (g === 5) return "5m";
  if (g === 15) return "15m";
  if (g === 60) return "1h";
  if (g === 1440) return "1d";
  return "5m";
}
function zonedDateTimeParts(ms, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    hourCycle: "h23",
  })
    .formatToParts(new Date(ms))
    .reduce((acc, p) => {
      if (p.type !== "literal") acc[p.type] = p.value;
      return acc;
    }, {});
  let hour = parts.hour === "24" ? "00" : parts.hour;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${hour}:${parts.minute}`,
  };
}

function zonedClockParts(ms, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
    hourCycle: "h23",
  })
    .formatToParts(new Date(ms))
    .reduce((acc, p) => {
      if (p.type !== "literal") acc[p.type] = p.value;
      return acc;
    }, {});
  const hour = parts.hour === "24" ? 0 : Number(parts.hour);
  return {
    month: Number(parts.month),
    day: Number(parts.day),
    hour,
    minute: Number(parts.minute),
  };
}

function formatClock(hour24, minute, includeMinute, compact = false) {
  const h = ((hour24 % 24) + 24) % 24;
  const suffix = compact ? (h < 12 ? "A" : "P") : (h < 12 ? "AM" : "PM");
  const display = h % 12 === 0 ? 12 : h % 12;
  if (includeMinute) {
    const gap = compact ? "" : " ";
    return `${display}:${String(minute).padStart(2, "0")}${gap}${suffix}`;
  }
  return compact ? `${display}${suffix}` : `${display} ${suffix}`;
}

function formatUnpaddedDate(ms, timeZone) {
  const { month, day } = zonedClockParts(ms, timeZone);
  return `${month}/${day}`;
}

function formatAxisLabel(ms, timeZone, granMinutes, labelEveryHours) {
  if (granMinutes === 1440) return formatUnpaddedDate(ms, timeZone);
  const { hour, minute } = zonedClockParts(ms, timeZone);
  if (minute !== 0) return "";
  if (hour === 0) return `${formatUnpaddedDate(ms, timeZone)}, 12A`;
  if (labelEveryHours > 1 && hour % labelEveryHours !== 0) return "";
  return formatClock(hour, 0, false, true);
}

function zonedLocalToUtcMs(ymd, hour, minute, timeZone) {
  const naive = `${ymd}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`;
  const utcGuess = Date.parse(`${naive}Z`);
  if (Number.isNaN(utcGuess)) return utcGuess;
  const wall = zonedDateTimeParts(utcGuess, timeZone);
  const asUtc = Date.parse(`${wall.date}T${wall.time}:00Z`);
  return utcGuess + (utcGuess - asUtc);
}

function localSixHourFloorMs(ms, timeZone) {
  const { date } = zonedDateTimeParts(ms, timeZone);
  const { hour } = zonedClockParts(ms, timeZone);
  const alignedHour = Math.floor(hour / 6) * 6;
  return zonedLocalToUtcMs(date, alignedHour, 0, timeZone);
}

function formatTooltipHeader(ms, timeZone, granMinutes) {
  if (ms == null) return "";
  if (granMinutes === 1440) return formatUnpaddedDate(ms, timeZone);
  const { hour, minute } = zonedClockParts(ms, timeZone);
  const time = formatClock(hour, minute, granMinutes < 60);
  return `${formatUnpaddedDate(ms, timeZone)}, ${time}`;
}

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return h >>> 0;
}

// Fixed colors for known catalog apps — spaced for clear chart contrast.
const APPLICATION_COLORS = {
  AIxURO: "#2563eb", // blue
  AIxTHY: "#ea580c", // orange
};

// Fallback palette for other/new apps (avoids blue/orange used above).
const APPLICATION_FALLBACK_PALETTE = [
  "#059669", // emerald
  "#7c3aed", // violet
  "#db2777", // pink
  "#0891b2", // cyan
  "#ca8a04", // yellow
  "#4f46e5", // indigo
  "#be123c", // rose
  "#0f766e", // teal
];

function colorForApplication(id) {
  if (!id) return APPLICATION_COLORS.AIxURO;
  const key = String(id);
  if (APPLICATION_COLORS[key]) return APPLICATION_COLORS[key];
  return APPLICATION_FALLBACK_PALETTE[hashString(key) % APPLICATION_FALLBACK_PALETTE.length];
}

function bucketWindowForLookup(startMs, granMinutes, timeZone) {
  const start = zonedDateTimeParts(startMs, timeZone);
  if (granMinutes === 1440) {
    return {
      startDate: start.date,
      startTime: start.time,
      endDate: start.date,
      endTime: "23:59",
    };
  }
  const endInclusiveMs = startMs + granMinutes * 60 * 1000 - 60 * 1000;
  const end = zonedDateTimeParts(endInclusiveMs, timeZone);
  return {
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
  };
}

const TIPS = {
  metric:
    "Jobs Submitted counts slides when processing starts. Jobs Processed counts slides when processing finishes. Counts are for your organization only.",
  range: "How far back to show, ending now, in the selected timezone.",
  granularity:
    "Size of each chart bucket. Ranges of 1 month or longer use at least 1-hour buckets.",
  chartType: "Draw the series as a line or as bars. Multiple applications stack as colored bars.",
  application:
    "Limit the chart to one or more applications. Empty means all applications enabled for your organization, each shown as its own series.",
  timezone:
    "Timezone for the search window and chart axis. Region narrows the zone list.",
  includePartialDay:
    "When checked, include the current incomplete bucket (today, when viewing by day). When unchecked, hide that partial bucket.",
  autoRefresh: "Reload the chart on this interval, or only when you click Refresh.",
  refresh: "Reload the chart now using the current controls.",
  chartSubmitted:
    "Each series is one application (color-coded). Bars stack when more than one application is shown.\n\nTimes use the selected timezone. Hover for per-app counts. Click a non-zero bar to open Slide Lookup for that window and application.\n\nIntra-day axis ticks are every 6 hours (12A, 6A, 12P, 6P).",
  chartProcessed:
    "Each series is one application (color-coded). Bars stack when more than one application is shown.\n\nTimes use the selected timezone. Hover for per-app counts. This metric does not open Slide Lookup.\n\nIntra-day axis ticks are every 6 hours (12A, 6A, 12P, 6P).",
};

const panelStyle = { border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" };
const inputStyle = { height: 36, padding: "6px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 };
const labelRowStyle = { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151" };
const fieldStyle = { display: "flex", flexDirection: "column", gap: 4, fontSize: 12 };

function ControlTip({ label, tip, align, wide, children }) {
  return (
    <div style={fieldStyle}>
      <span style={labelRowStyle}>
        {label}
        <InfoTip text={tip} align={align} wide={wide} />
      </span>
      {children}
    </div>
  );
}

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [refreshSeq, setRefreshSeq] = useState(0);
  const [metric, setMetric] = useState("submitted");
  const [rangeKey, setRangeKey] = useState("1w");
  const [granKey, setGranKey] = useState("1h");
  const [chartType, setChartType] = useState("bar");
  const [tzRegion, setTzRegion] = useState("Americas");
  const [tz, setTz] = useState("America/Los_Angeles");
  const [includePartialDay, setIncludePartialDay] = useState(true);
  const [refreshKey, setRefreshKey] = useState("off");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [payload, setPayload] = useState(null);
  const [applications, setApplications] = useState([]);
  const [applicationOptions, setApplicationOptions] = useState([
    { id: "AIxURO", label: "AIxURO" },
  ]);

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const initialTz = Object.values(TIMEZONES_BY_REGION).flat().includes(browserTz) ? browserTz : "America/Los_Angeles";

  useEffect(() => {
    if (!tz) setTz(initialTz);
  }, []);

  useEffect(() => {
    authedGet("/customer/profile")
      .then((data) => {
        const enabled = Array.isArray(data?.enabled_applications)
          ? data.enabled_applications.map(String)
          : ["AIxURO"];
        setApplicationOptions(
          (enabled.length ? enabled : ["AIxURO"]).map((id) => ({
            id,
            label: id,
          }))
        );
      })
      .catch(() => {
        /* keep AIxURO default */
      });
  }, []);

  useEffect(() => {
    for (const [region, zones] of Object.entries(TIMEZONES_BY_REGION)) {
      if (zones.includes(tz)) {
        setTzRegion(region);
        break;
      }
    }
  }, [tz]);

  const effectiveGranKey = useMemo(() => {
    if (rangeKey === "custom") return granKey;
    return enforceGranularity(rangeKey, granKey);
  }, [rangeKey, granKey]);

  const queryPath = useMemo(() => {
    const params = new URLSearchParams();
    params.set("metric", metric);
    params.set("granularity", effectiveGranKey);
    params.set("tz", tz);
    params.set("include_partial_day", includePartialDay ? "true" : "false");
    params.set("range", rangeKey);
    params.set("end", "now");
    if (applications.length) {
      params.set("applications", applications.join(","));
    }
    return `/customer/dash/jobs-metrics?${params.toString()}`;
  }, [metric, rangeKey, effectiveGranKey, tz, includePartialDay, applications]);

  const fetchData = React.useCallback(() => {
    const controller = new AbortController();
    setLoading(true);
    setErr("");
    authedGet(queryPath, controller.signal)
      .then((data) => setPayload(data))
      .catch((e) => {
        if (e.name !== "AbortError") setErr(String(e.message || e));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [queryPath]);

  useEffect(() => {
    const cleanup = fetchData();
    return cleanup;
  }, [fetchData, refreshSeq]);

  useEffect(() => {
    const opt = REFRESH_OPTS.find((o) => o.key === refreshKey);
    if (!opt || opt.ms === 0) return;
    const id = setInterval(() => setRefreshSeq((v) => v + 1), opt.ms);
    return () => clearInterval(id);
  }, [refreshKey]);

  const granularityMinutes = payload?.granularity_minutes;
  const labelEveryHours = 6;

  const option = useMemo(() => {
    if (!payload?.series?.length) {
      return {
        xAxis: { type: "time" },
        yAxis: { type: "value" },
        series: [],
      };
    }

    const list = payload.series;
    const multi = list.length > 1;
    const hourMs = 3600 * 1000;
    const isDaily = granularityMinutes === 1440;
    const firstPts = list.find((s) => (s.points || []).length)?.points || [];
    const axisMin = !isDaily && firstPts.length ? localSixHourFloorMs(firstPts[0][0], tz) : undefined;
    const stackBars = chartType === "bar" && multi;

    return {
      grid: {
        left: 50,
        right: multi ? 140 : 20,
        top: 24,
        bottom: 56,
      },
      legend: multi
        ? {
            type: "scroll",
            orient: "vertical",
            right: 8,
            top: "middle",
          }
        : undefined,
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: (items) => {
          const ts = items?.[0]?.data?.[0];
          const header = formatTooltipHeader(ts, tz, granularityMinutes);
          const lines = (items || []).map((it) => `${it.marker} ${it.seriesName}: <b>${it.data?.[1] ?? 0}</b>`);
          const total = (items || []).reduce((sum, it) => sum + (Number(it.data?.[1]) || 0), 0);
          if (multi && items?.length > 1) {
            lines.push(`Total: <b>${total}</b>`);
          }
          return [header, ...lines].join("<br/>");
        },
      },
      xAxis: {
        type: "time",
        min: axisMin,
        interval: isDaily ? 24 * hourMs : 6 * hourMs,
        minInterval: isDaily ? 24 * hourMs : 6 * hourMs,
        maxInterval: isDaily ? 24 * hourMs : 6 * hourMs,
        axisLabel: {
          hideOverlap: true,
          formatter: (val) => formatAxisLabel(val, tz, granularityMinutes, labelEveryHours),
        },
      },
      yAxis: { type: "value" },
      series: list.map((s) => {
        const appId = s.application || s.label || s.customer_id || "Jobs";
        const name = s.label || appId;
        const color = colorForApplication(s.application || appId);
        return {
          name,
          type: chartType,
          stack: stackBars ? "apps" : undefined,
          data: s.points || [],
          showSymbol: chartType === "line",
          itemStyle: { color },
          cursor: metric === "submitted" ? "pointer" : "default",
        };
      }),
    };
  }, [payload, granularityMinutes, metric, chartType, loading, tz, labelEveryHours]);

  const onChartClick = (params) => {
    if (metric !== "submitted") return;
    const data = params?.data;
    if (!Array.isArray(data) || data.length < 2) return;
    const startMs = data[0];
    const count = data[1];
    if (!startMs || !count) return;
    const gran = granularityMinutes || granMinutes(effectiveGranKey);
    const win = bucketWindowForLookup(startMs, gran, tz);
    const q = new URLSearchParams({
      timezone: tz,
      start_date: win.startDate,
      start_time: win.startTime,
      end_date: win.endDate,
      end_time: win.endTime,
    });
    const seriesList = payload?.series || [];
    const clicked = seriesList.find(
      (s) => (s.label || s.application) === params?.seriesName
    ) || seriesList[params?.seriesIndex];
    const app = clicked?.application;
    if (app) q.set("applications", app);
    navigate(`/slides?${q.toString()}`);
  };

  useEffect(() => {
    document.title = "Dashboard";
  }, []);

  return (
    <div style={{ padding: 16 }}>
      <div style={panelStyle}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
          <ControlTip label="Metric" tip={TIPS.metric} wide>
            <select style={inputStyle} value={metric} onChange={(e) => setMetric(e.target.value)}>
              {METRIC_OPTS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </ControlTip>
          <ControlTip label="Range" tip={TIPS.range}>
            <select style={inputStyle} value={rangeKey} onChange={(e) => setRangeKey(e.target.value)}>
              {RANGE_OPTS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </ControlTip>
          <ControlTip label="Granularity" tip={TIPS.granularity}>
            <select style={inputStyle} value={granKey} onChange={(e) => setGranKey(e.target.value)}>
              {GRAN_OPTS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </ControlTip>
          <ControlTip label="Chart" tip={TIPS.chartType}>
            <select style={inputStyle} value={chartType} onChange={(e) => setChartType(e.target.value)}>
              {CHART_OPTS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </ControlTip>
          <ControlTip label="Application" tip={TIPS.application} wide>
            <ApplicationMultiSelectDropdown
              options={applicationOptions}
              value={applications}
              onChange={setApplications}
            />
          </ControlTip>
          <ControlTip label="Timezone" tip={TIPS.timezone} wide>
            <div style={{ display: "flex", gap: 8 }}>
              <select
                style={inputStyle}
                value={tzRegion}
                onChange={(e) => {
                  const r = e.target.value;
                  setTzRegion(r);
                  const zones = TIMEZONES_BY_REGION[r] || [];
                  if (!zones.includes(tz) && zones.length) setTz(zones[0]);
                }}
              >
                {Object.keys(TIMEZONES_BY_REGION).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <select style={{ ...inputStyle, minWidth: 120 }} value={tz} onChange={(e) => setTz(e.target.value)}>
                {TIMEZONES_BY_REGION[tzRegion]?.map((zone) => (
                  <option key={zone} value={zone}>{formatTimezoneLabel(zone)}</option>
                ))}
              </select>
            </div>
          </ControlTip>
          <div style={{ ...fieldStyle, paddingBottom: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={includePartialDay} onChange={(e) => setIncludePartialDay(e.target.checked)} />
              Include current (partial) day
              <InfoTip text={TIPS.includePartialDay} />
            </label>
          </div>
          <ControlTip label="Auto-refresh" tip={TIPS.autoRefresh} align="end">
            <select style={inputStyle} value={refreshKey} onChange={(e) => setRefreshKey(e.target.value)}>
              {REFRESH_OPTS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </ControlTip>
          <div style={{ display: "flex", alignItems: "center", gap: 6, paddingBottom: 0 }}>
            <button disabled={loading} style={{ ...inputStyle, cursor: "pointer" }} onClick={() => setRefreshSeq((v) => v + 1)}>
              {loading ? "Loading…" : "Refresh"}
            </button>
            <InfoTip text={TIPS.refresh} align="end" />
          </div>
        </div>
      </div>

      {loading && <div style={{ padding: 12 }}>Loading…</div>}
      {err && <div style={{ padding: 12, color: "#b91c1c" }}>{err}</div>}

      {!loading && !err && payload && (
        <div style={{ ...panelStyle, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {METRIC_OPTS.find((m) => m.key === metric)?.label || "Jobs"}
            </span>
            <InfoTip
              wide
              text={metric === "submitted" ? TIPS.chartSubmitted : TIPS.chartProcessed}
            />
          </div>
          <ReactECharts
            key={[refreshSeq, metric, rangeKey, effectiveGranKey, tz, includePartialDay, applications.join(",")].join("|")}
            option={option}
            notMerge={true}
            lazyUpdate={false}
            style={{ height: 400 }}
            onEvents={{ click: onChartClick }}
          />
        </div>
      )}
    </div>
  );
}
